import { BottomSheetTextInput } from '@gorhom/bottom-sheet'
import { StyleSheet } from 'react-native'

import {
  FigmaAlertCircleIcon,
  FigmaSuccessCheckIcon,
} from '../icons/FigmaSystemIcons'
import { useFunkitTranslation } from '../providers/FunkitI18nProvider'
import { Box, Text, useTheme } from '../theme/restyle'
import { PillButton } from './PillButton'

const STATUS_ICON_SIZE = 16

export interface WalletAddressInputProps {
  value: string
  onChangeText: (value: string) => void
  /**
   * Whether what has been typed is an address at all. `undefined` while the
   * field is empty, where the design shows no verdict either way.
   */
  isAddress: boolean | undefined
  /** Fills the field from the clipboard. */
  onPaste: () => void
  /**
   * A clipboard read is out. The read can block for seconds on the OS
   * permission prompt, so Paste is held down until it lands rather than letting
   * a second tap queue a second read.
   */
  isPasting?: boolean
  /**
   * Fills the field with the connected wallet's own address. Omit to drop the
   * shortcut (see `disableConnectedWallet` on the flow config).
   */
  onUseConnected?: () => void
  /** A reason the address can't be used, past being well-formed. */
  errorText?: string
  /** Non-blocking caution, e.g. a flagged-address notice. */
  warningText?: string
}

/**
 * The recipient field: the address, its verdict, and the two ways to fill it.
 *
 * `Paste` is the primary action while the field is empty — an address is pasted
 * far more often than typed — and gives way to the quieter pair once there is
 * something to clear.
 */
export function WalletAddressInput({
  value,
  onChangeText,
  isAddress,
  onPaste,
  isPasting = false,
  onUseConnected,
  errorText,
  warningText,
}: WalletAddressInputProps) {
  const { t } = useFunkitTranslation()
  const theme = useTheme()
  const label = t('withdrawCrypto.addressPlaceholder')
  const isEmpty = value === ''

  // An error outranks a warning: both describe the same field, and the blocking
  // one is what the user has to act on.
  const helperText = errorText ?? warningText

  return (
    <Box style={styles.root}>
      <Box
        flexDirection="row"
        alignItems="center"
        justifyContent="space-between"
        style={styles.header}
      >
        <Text
          variant="detail"
          weight="medium"
          numberOfLines={1}
          style={[styles.label, { color: theme.content.elements.secondary }]}
        >
          {label}
        </Text>
        {isAddress === undefined ? null : (
          <Box flexDirection="row" alignItems="center" style={styles.status}>
            {isAddress ? (
              <FigmaSuccessCheckIcon size={STATUS_ICON_SIZE} />
            ) : (
              <FigmaAlertCircleIcon
                size={STATUS_ICON_SIZE}
                color={theme.content.elements.error}
              />
            )}
            <Text
              variant="detail"
              weight="medium"
              style={{
                color: isAddress
                  ? theme.content.elements.success
                  : theme.content.elements.error,
              }}
            >
              {isAddress
                ? t('withdrawAddress.detected')
                : t('withdrawAddress.notFound')}
            </Text>
          </Box>
        )}
      </Box>

      <Box
        borderRadius="element"
        style={[
          styles.field,
          {
            height: theme.sizes.buttonLg,
            borderColor: theme.border.elements.default.color,
            borderWidth: theme.border.elements.default.weight,
            backgroundColor: theme.background.modal,
          },
        ]}
      >
        {/* One line, not wrapped: the verdict beside the label is what says the
            address is complete, so the field doesn't have to show all of it. */}
        <BottomSheetTextInput
          value={value}
          onChangeText={onChangeText}
          placeholder={label}
          placeholderTextColor={theme.content.elements.secondary}
          accessibilityLabel={label}
          autoCapitalize="none"
          autoCorrect={false}
          spellCheck={false}
          style={[
            styles.input,
            theme.textVariants.bodyMedium,
            { color: theme.content.elements.primary },
          ]}
        />
      </Box>

      <Box flexDirection="row" style={styles.buttonRow}>
        <Box style={styles.button}>
          <PillButton
            label={t('withdrawAddress.paste')}
            size="buttonMd"
            variant={isEmpty ? 'primary' : 'secondary'}
            onPress={onPaste}
            disabled={isPasting}
          />
        </Box>
        <Box style={styles.button}>
          {isEmpty ? (
            onUseConnected ? (
              <PillButton
                label={t('withdrawAddress.useConnected')}
                size="buttonMd"
                variant="secondary"
                onPress={onUseConnected}
              />
            ) : null
          ) : (
            <PillButton
              label={t('withdrawAddress.clear')}
              size="buttonMd"
              variant="secondary"
              onPress={() => onChangeText('')}
            />
          )}
        </Box>
      </Box>

      {helperText ? (
        <Text
          variant="detail"
          style={{
            color: errorText
              ? theme.content.elements.error
              : theme.content.elements.warning,
          }}
        >
          {helperText}
        </Text>
      ) : null}
    </Box>
  )
}

const styles = StyleSheet.create({
  root: { gap: 8 },
  header: { gap: 8 },
  label: { flexShrink: 1 },
  status: { gap: 4 },
  // The design's own asymmetry: the text is inset 16 and the (unused) trailing
  // slot 8, so the field's own padding is what centres the single line.
  field: { paddingLeft: 16, paddingRight: 8, justifyContent: 'center' },
  input: { padding: 0 },
  buttonRow: { gap: 10 },
  // Both halves keep their width whether or not the second one has a button, so
  // Paste doesn't stretch across the row when it stands alone.
  button: { flex: 1 },
})
